/**
 * Locale constants and pure helpers (no React). Safe to import from data
 * files like src/assets/products.ts and utils.
 */
export const LOCALES = ['en', 'km'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';

/** Labels shown in the language switcher. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  km: 'ខ្មែរ',
};

/** Open Graph locale codes (og:locale). */
export const OG_LOCALE: Record<Locale, string> = {
  en: 'en_US',
  km: 'km_KH',
};

/** localStorage key remembering the visitor's language choice. */
export const LANGUAGE_STORAGE_KEY = 'akas-locale';

export function isLocale(value: string | null | undefined): value is Locale {
  return !!value && (LOCALES as readonly string[]).includes(value);
}

/** Detect the locale from a router pathname (first URL segment). */
export function localeFromPathname(pathname: string): Locale {
  const segment = pathname.split('/')[1];
  return isLocale(segment) ? segment : DEFAULT_LOCALE;
}

/** Prefix a site path with the locale, e.g. ('en', '/about-us') → '/en/about-us'. */
export function localePath(locale: Locale, path: string): string {
  const suffix = !path || path === '/' ? '' : path;
  return `/${locale}${suffix}`;
}

/** Best guess from storage/browser when a URL has no locale segment. */
export function preferredLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // localStorage may be unavailable (private mode); fall through
  }
  if (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('km')) {
    return 'km';
  }
  return DEFAULT_LOCALE;
}
